/**
 * DocumentRefresh - the personal refresh workspace inside the
 * Document Editor (Princewill's own-documents use case, approved
 * 2026-11-05).
 *
 * The owner's uploaded page (PDF page 1 or a photo/scan) is the
 * background. Everything he does happens ON TOP of it, by hand:
 * text boxes for new details, opaque cover patches (digital
 * white-out) and redaction blocks, highlights, and his kept
 * logos/signatures/pictures placed from the shared libraries.
 * Cleanup is brightness/contrast, rotate and crop - plain canvas
 * operations on the whole page.
 *
 * The boundary this surface is built inside (the owner's own
 * accepted line): NOTHING here analyses the uploaded page. No
 * text detection, no OCR, no content-aware fill, no automatic
 * removal of anything - a cover is an opaque rectangle the user
 * places and sizes himself, and it stays part of the document,
 * exactly like physical white-out. The background never goes
 * near the protected clone engines.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    RefreshState, RefreshOverlay, makeRefreshOverlay, placeRefreshImage,
    clampOverlayToPage, rotateRefreshGeometry, cropRefreshGeometry,
    rotateImageDataUrl, cropImageDataUrl, refreshFilterCss,
    exportRefreshPng, exportRefreshPdf, refreshExportFacts,
    createRefreshFromImageFile, createRefreshFromPdfFile
} from '../utils/refreshDocument';
import { loadAssetLibrary, AssetKind } from '../utils/assetLibrary';

export interface DocumentRefreshColors {
    text: string; textSecondary: string; border: string; card: string; bg: string; primary: string;
}

interface DocumentRefreshProps {
    state: RefreshState;
    onChange: (next: RefreshState) => void;
    onExit: () => void;
    colors: DocumentRefreshColors;
}

const btnStyle = (colors: DocumentRefreshColors, active = false): React.CSSProperties => ({
    padding: '7px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
    background: active ? colors.primary : colors.bg, color: active ? '#FFF' : colors.text,
    border: `1px solid ${active ? colors.primary : colors.border}`
});

const labelStyle: React.CSSProperties = { display: 'block', fontSize: '11px', fontWeight: '500', marginBottom: '3px' };

export const DocumentRefresh: React.FC<DocumentRefreshProps> = ({ state, onChange, onExit, colors }) => {
    const page = state.background;
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [exportBusy, setExportBusy] = useState(false);
    const [exportNote, setExportNote] = useState<string | null>(null);
    const [busyNote, setBusyNote] = useState<string | null>(null);
    const [sampleArmed, setSampleArmed] = useState(false);
    const [crop, setCrop] = useState({ left: 0, top: 0, right: 0, bottom: 0 });
    const stateRef = useRef(state);
    stateRef.current = state;
    const sampleCanvasRef = useRef<HTMLCanvasElement | null>(null);

    const selected = state.overlays.find(o => o.id === selectedId) || null;
    const facts = refreshExportFacts(state);

    // --- Scale-to-fit stage (the template stage's pattern) ---
    const stageRef = useRef<HTMLDivElement>(null);
    const [stageScale, setStageScale] = useState(1);
    useEffect(() => {
        const el = stageRef.current;
        if (!el) return;
        const measure = () => {
            const w = el.clientWidth;
            if (w > 0) setStageScale(Math.min(1, w / Math.max(1, page.width)));
        };
        measure();
        const RO = (window as any).ResizeObserver;
        if (!RO) return;
        const ro = new RO(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [page.width]);

    const updateOverlay = (id: string, patch: Partial<RefreshOverlay>) => {
        const cur = stateRef.current;
        onChange({ ...cur, overlays: cur.overlays.map(o => (o.id === id ? clampOverlayToPage({ ...o, ...patch }, cur.background) : o)) });
    };

    const addOverlay = (kind: RefreshOverlay['kind']) => {
        const o = makeRefreshOverlay(kind, page);
        onChange({ ...state, overlays: [...state.overlays, o] });
        setSelectedId(o.id);
    };

    const placeFromLibrary = (kind: AssetKind, entryId: string) => {
        const entry = loadAssetLibrary(kind).find(e => e.id === entryId);
        if (!entry) return;
        const probe = new Image();
        probe.onload = () => {
            const o = placeRefreshImage(page, entry.src, entry.name, { width: probe.naturalWidth || probe.width, height: probe.naturalHeight || probe.height });
            onChange({ ...stateRef.current, overlays: [...stateRef.current.overlays, o] });
            setSelectedId(o.id);
        };
        probe.onerror = () => {
            const o = placeRefreshImage(page, entry.src, entry.name);
            onChange({ ...stateRef.current, overlays: [...stateRef.current.overlays, o] });
            setSelectedId(o.id);
        };
        probe.src = entry.src;
    };

    const deleteSelected = () => {
        if (!selected) return;
        onChange({ ...state, overlays: state.overlays.filter(o => o.id !== selected.id) });
        setSelectedId(null);
    };

    // --- Drag: pointer capture on the overlay, deltas in page px ---
    const dragRef = useRef<{ id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);
    const onOverlayPointerDown = (e: React.PointerEvent, o: RefreshOverlay) => {
        e.stopPropagation();
        setSelectedId(o.id);
        try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* best-effort */ }
        dragRef.current = { id: o.id, startX: e.clientX, startY: e.clientY, origX: o.x, origY: o.y };
    };
    const onOverlayPointerMove = (e: React.PointerEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dx = (e.clientX - drag.startX) / Math.max(0.05, stageScale);
        const dy = (e.clientY - drag.startY) / Math.max(0.05, stageScale);
        updateOverlay(drag.id, { x: Math.round(drag.origX + dx), y: Math.round(drag.origY + dy) });
    };
    const onOverlayPointerUp = () => { dragRef.current = null; };

    // --- Baked page edits: rotate / crop re-render the picture ---
    const applyRotate = async (dir: 'cw' | 'ccw') => {
        if (!page.src || busyNote) return;
        setBusyNote('Rotating…');
        try {
            const src = await rotateImageDataUrl(page.src, dir);
            const geo = rotateRefreshGeometry(state.overlays, page.width, page.height, dir);
            sampleCanvasRef.current = null;
            onChange({ ...state, background: { ...page, src, width: geo.width, height: geo.height }, overlays: geo.overlays });
        } catch { setExportNote('Rotate failed in this browser - the page was left as it was.'); }
        finally { setBusyNote(null); }
    };

    const applyCrop = async () => {
        if (!page.src || busyNote) return;
        const rect = {
            x: Math.round((crop.left / 100) * page.width),
            y: Math.round((crop.top / 100) * page.height),
            width: Math.round(((100 - crop.left - crop.right) / 100) * page.width),
            height: Math.round(((100 - crop.top - crop.bottom) / 100) * page.height)
        };
        if (rect.width < 20 || rect.height < 20) { setExportNote('That crop leaves almost nothing - widen it and try again.'); return; }
        setBusyNote('Cropping…');
        try {
            const src = await cropImageDataUrl(page.src, rect);
            const geo = cropRefreshGeometry(state.overlays, rect);
            sampleCanvasRef.current = null;
            onChange({ ...state, background: { ...page, src, width: geo.width, height: geo.height }, overlays: geo.overlays });
            setCrop({ left: 0, top: 0, right: 0, bottom: 0 });
        } catch { setExportNote('Crop failed in this browser - the page was left as it was.'); }
        finally { setBusyNote(null); }
    };

    // --- Eyedropper: sample a page colour for the cover fill ---
    const armSample = () => {
        if (!page.src) return;
        const img = new Image();
        img.onload = () => {
            try {
                const c = document.createElement('canvas');
                c.width = page.width; c.height = page.height;
                const ctx = c.getContext('2d');
                if (!ctx) return;
                ctx.drawImage(img, 0, 0, page.width, page.height);
                sampleCanvasRef.current = c;
                setSampleArmed(true);
            } catch { /* sampling unavailable: the colour input still works */ }
        };
        img.src = page.src;
    };

    const onStageClick = (e: React.MouseEvent) => {
        if (!sampleArmed || !selected || selected.kind !== 'cover') { setSelectedId(null); return; }
        const stage = stageRef.current;
        const canvas = sampleCanvasRef.current;
        if (!stage || !canvas) { setSampleArmed(false); return; }
        const rect = stage.querySelector('[data-refresh-page]')?.getBoundingClientRect();
        if (!rect) { setSampleArmed(false); return; }
        const px = Math.min(page.width - 1, Math.max(0, Math.floor(((e.clientX - rect.left) / Math.max(1, rect.width)) * page.width)));
        const py = Math.min(page.height - 1, Math.max(0, Math.floor(((e.clientY - rect.top) / Math.max(1, rect.height)) * page.height)));
        try {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                const d = ctx.getImageData(px, py, 1, 1).data;
                const hex = `#${[d[0], d[1], d[2]].map(v => v.toString(16).padStart(2, '0')).join('')}`;
                updateOverlay(selected.id, { fill: hex });
            }
        } catch { /* pixel read blocked: no-op */ }
        setSampleArmed(false);
    };

    const handleExport = async (fmt: 'pdf' | 'png') => {
        if (exportBusy) return;
        setExportBusy(true);
        setExportNote(null);
        try {
            const r = fmt === 'pdf' ? await exportRefreshPdf(state) : await exportRefreshPng(state);
            setExportNote(`Exported ${fmt.toUpperCase()} — ${r.widthPx} × ${r.heightPx} px${fmt === 'pdf' ? `, page ${r.sizeMm.w} × ${r.sizeMm.h} mm at ${r.pdfDpi} DPI` : ''} (${r.filename})`);
        } catch (err) {
            console.error('Refresh export failed:', err);
            setExportNote('Export failed — the file could not be generated in this browser. Nothing was downloaded.');
        } finally { setExportBusy(false); }
    };

    const handleReplaceBackground = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || busyNote) return;
        setBusyNote('Loading your page…');
        try {
            const fresh = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
                ? await createRefreshFromPdfFile(file)
                : await createRefreshFromImageFile(file);
            const kept = state.overlays.map(o => clampOverlayToPage(o, fresh.background));
            onChange({ ...fresh, overlays: kept });
            setSelectedId(null);
            sampleCanvasRef.current = null;
        } catch {
            setExportNote('That file could not be read as a PDF or image - your current page was left untouched.');
        } finally { setBusyNote(null); }
    };

    const libraryKinds: Array<{ kind: AssetKind; label: string }> = [
        { kind: 'logos', label: '🏷️ Logos' },
        { kind: 'signatures', label: '✒️ Signatures' },
        { kind: 'pictures', label: '🖼️ Pictures' }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                <div>
                    <h3 style={{ margin: '0 0 2px', color: colors.text, fontSize: '15px' }}>🔄 Refresh my document</h3>
                    <p style={{ margin: 0, color: colors.textSecondary, fontSize: '12px' }}>
                        Your page, edited by hand: cover a stale detail, type the new one, place your logo or signature, print.
                    </p>
                </div>
                <button type="button" onClick={onExit} style={btnStyle(colors)}>← Back to templates</button>
            </div>

            <div className="oc-g2" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
                {/* Stage */}
                <div style={{ background: colors.card, borderRadius: '12px', padding: '20px', border: `1px solid ${colors.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                        <input
                            type="text"
                            aria-label="Document name"
                            value={state.name}
                            onChange={e => onChange({ ...state, name: e.target.value })}
                            style={{ padding: '6px 8px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: '13px', fontWeight: '600', minWidth: '200px' }}
                        />
                        <span style={{ color: colors.textSecondary, fontSize: '11px' }}>
                            {page.sourceKind === 'pdf' ? 'PDF page 1' : 'Photo / scan'} · {page.width} × {page.height} px
                        </span>
                    </div>
                    <div ref={stageRef} style={{ width: '100%', height: page.height * stageScale, position: 'relative', overflow: 'hidden' }} onClick={onStageClick}>
                        <div
                            data-refresh-page
                            style={{
                                width: page.width, height: page.height, margin: '0 auto', position: 'relative',
                                transform: `scale(${stageScale})`, transformOrigin: 'top center',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)', background: '#FFF', overflow: 'hidden'
                            }}
                        >
                            {page.src ? (
                                <img
                                    src={page.src}
                                    alt="Your uploaded page"
                                    draggable={false}
                                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', filter: refreshFilterCss(page) || undefined, userSelect: 'none' }}
                                />
                            ) : (
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '20px', textAlign: 'center' }}>
                                    <span style={{ fontSize: '26px' }}>📄</span>
                                    <span style={{ color: '#6B7280', fontSize: '13px' }}>
                                        This saved design's page picture was too large to keep in storage, so only your edits were kept.
                                        Re-upload the same page to carry on - your overlays are still here.
                                    </span>
                                    <input type="file" accept="image/*,application/pdf" aria-label="Re-upload your page" onChange={handleReplaceBackground} style={{ fontSize: '12px' }} />
                                </div>
                            )}
                            {state.overlays.map(o => (
                                <div
                                    key={o.id}
                                    data-refresh-overlay={o.id}
                                    data-overlay-kind={o.kind}
                                    onPointerDown={e => onOverlayPointerDown(e, o)}
                                    onPointerMove={onOverlayPointerMove}
                                    onPointerUp={onOverlayPointerUp}
                                    onPointerCancel={onOverlayPointerUp}
                                    style={{
                                        position: 'absolute', left: o.x, top: o.y, width: o.width, height: o.height,
                                        cursor: 'move', touchAction: 'none',
                                        outline: selectedId === o.id ? '2px solid #3B82F6' : '1px dashed rgba(59,130,246,0.35)',
                                        background: o.kind === 'cover' ? (o.fill || '#FFFFFF')
                                            : o.kind === 'redact' ? '#000'
                                            : o.kind === 'highlight' ? (o.fill || '#FFEB3B')
                                            : 'transparent',
                                        opacity: o.kind === 'highlight' ? 0.35 : 1,
                                        overflow: 'hidden'
                                    }}
                                >
                                    {o.kind === 'image' && o.src && (
                                        <img src={o.src} alt={o.assetName || 'Placed image'} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
                                    )}
                                    {o.kind === 'text' && (
                                        <span style={{
                                            display: 'block', padding: '2px', whiteSpace: 'pre-wrap', pointerEvents: 'none',
                                            fontSize: o.fontSize || 16, fontFamily: o.fontFamily || 'Arial', fontWeight: o.bold ? 'bold' : 'normal', color: o.color || '#111111', lineHeight: 1.25
                                        }}>
                                            {o.text || <span style={{ opacity: 0.45 }}>Type in the panel →</span>}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    {sampleArmed && <p style={{ color: colors.primary, fontSize: '12px', margin: '8px 0 0' }}>Tap the page where the cover patch should match…</p>}
                    {busyNote && <p style={{ color: colors.textSecondary, fontSize: '12px', margin: '8px 0 0' }}>{busyNote}</p>}
                </div>

                {/* Tools */}
                <div style={{ background: colors.card, borderRadius: '12px', padding: '16px', border: `1px solid ${colors.border}`, maxHeight: '640px', overflowY: 'auto' }}>
                    <div style={{ marginBottom: '14px' }}>
                        <span style={{ ...labelStyle, color: colors.text }}>Add on top of your page</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            <button type="button" style={btnStyle(colors)} onClick={() => addOverlay('text')}>✏️ Text box</button>
                            <button type="button" style={btnStyle(colors)} onClick={() => addOverlay('cover')}>⬜ Cover patch</button>
                            <button type="button" style={btnStyle(colors)} onClick={() => addOverlay('highlight')}>🖍️ Highlight</button>
                            <button type="button" style={btnStyle(colors)} onClick={() => addOverlay('redact')}>⬛ Redaction block</button>
                        </div>
                        <p style={{ margin: '6px 0 0', color: colors.textSecondary, fontSize: '11px' }}>
                            A cover patch is digital white-out: an opaque patch you place and size yourself.
                            Nothing on your page is detected or removed for you.
                        </p>
                    </div>

                    <div style={{ marginBottom: '14px', borderTop: `1px solid ${colors.border}`, paddingTop: '10px' }}>
                        <span style={{ ...labelStyle, color: colors.text }}>Place from your libraries</span>
                        {libraryKinds.map(({ kind, label }) => {
                            const entries = loadAssetLibrary(kind);
                            if (!entries.length) return null;
                            return (
                                <div key={kind} style={{ marginBottom: '6px' }}>
                                    <span style={{ fontSize: '11px', color: colors.textSecondary }}>{label}</span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                                        {entries.map(entry => (
                                            <button
                                                key={entry.id}
                                                type="button"
                                                aria-label={`Place ${entry.name} on page`}
                                                onClick={() => placeFromLibrary(kind, entry.id)}
                                                style={{ width: '52px', height: '38px', padding: '2px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: '#FFF', cursor: 'pointer', overflow: 'hidden' }}
                                                title={entry.name}
                                            >
                                                <img src={entry.src} alt={entry.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: 'auto' }} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                        {libraryKinds.every(({ kind }) => loadAssetLibrary(kind).length === 0) && (
                            <p style={{ margin: 0, color: colors.textSecondary, fontSize: '11px' }}>Nothing kept yet - add logos, signatures and pictures from the template view's Library tab; they live in the same libraries.</p>
                        )}
                    </div>

                    {selected && (
                        <div style={{ marginBottom: '14px', borderTop: `1px solid ${colors.border}`, paddingTop: '10px' }}>
                            <span style={{ ...labelStyle, color: colors.text }}>
                                Selected: {selected.kind === 'text' ? 'Text box' : selected.kind === 'cover' ? 'Cover patch' : selected.kind === 'highlight' ? 'Highlight' : selected.kind === 'redact' ? 'Redaction block' : (selected.assetName || 'Placed image')}
                            </span>
                            {selected.kind === 'text' && (
                                <>
                                    <textarea
                                        aria-label="Overlay text"
                                        value={selected.text || ''}
                                        onChange={e => updateOverlay(selected.id, { text: e.target.value })}
                                        rows={2}
                                        style={{ width: '100%', padding: '7px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: '12px', marginBottom: '6px' }}
                                    />
                                    <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                                        <select
                                            aria-label="Overlay font"
                                            value={selected.fontFamily || 'Arial'}
                                            onChange={e => updateOverlay(selected.id, { fontFamily: e.target.value })}
                                            style={{ flex: 1, padding: '6px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: '12px' }}
                                        >
                                            {['Arial', 'Georgia', 'Times New Roman', 'Courier New'].map(f => <option key={f} value={f}>{f}</option>)}
                                        </select>
                                        <input
                                            type="color"
                                            aria-label="Overlay text colour"
                                            value={/^#[0-9a-f]{6}$/i.test(selected.color || '') ? selected.color : '#111111'}
                                            onChange={e => updateOverlay(selected.id, { color: e.target.value })}
                                            style={{ width: '40px', height: '30px', padding: 0, border: `1px solid ${colors.border}`, borderRadius: '6px', background: colors.bg, cursor: 'pointer' }}
                                        />
                                        <button type="button" aria-pressed={!!selected.bold} onClick={() => updateOverlay(selected.id, { bold: !selected.bold })} style={btnStyle(colors, !!selected.bold)}>B</button>
                                    </div>
                                    <label style={{ ...labelStyle, color: colors.textSecondary }}>Text size — {selected.fontSize || 16}px</label>
                                    <input type="range" min={8} max={120} step={1} aria-label="Overlay text size" value={selected.fontSize || 16} onChange={e => updateOverlay(selected.id, { fontSize: Number(e.target.value) })} style={{ width: '100%' }} />
                                </>
                            )}
                            {selected.kind === 'cover' && (
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                                    <input
                                        type="color"
                                        aria-label="Cover fill colour"
                                        value={/^#[0-9a-f]{6}$/i.test(selected.fill || '') ? selected.fill : '#ffffff'}
                                        onChange={e => updateOverlay(selected.id, { fill: e.target.value })}
                                        style={{ width: '40px', height: '30px', padding: 0, border: `1px solid ${colors.border}`, borderRadius: '6px', background: colors.bg, cursor: 'pointer' }}
                                    />
                                    <span style={{ fontSize: '11px', color: colors.textSecondary }}>{selected.fill || '#FFFFFF'}</span>
                                    <button type="button" onClick={armSample} style={btnStyle(colors, sampleArmed)}>🎯 Sample page colour</button>
                                </div>
                            )}
                            {selected.kind === 'highlight' && (
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                                    <input
                                        type="color"
                                        aria-label="Highlight colour"
                                        value={/^#[0-9a-f]{6}$/i.test(selected.fill || '') ? selected.fill : '#ffeb3b'}
                                        onChange={e => updateOverlay(selected.id, { fill: e.target.value })}
                                        style={{ width: '40px', height: '30px', padding: 0, border: `1px solid ${colors.border}`, borderRadius: '6px', background: colors.bg, cursor: 'pointer' }}
                                    />
                                    <span style={{ fontSize: '11px', color: colors.textSecondary }}> translucent</span>
                                </div>
                            )}
                            <label style={{ ...labelStyle, color: colors.textSecondary }}>Width — {selected.width}px</label>
                            <input type="range" min={16} max={page.width} step={1} aria-label="Overlay width" value={selected.width} onChange={e => updateOverlay(selected.id, { width: Number(e.target.value) })} style={{ width: '100%' }} />
                            <label style={{ ...labelStyle, color: colors.textSecondary }}>Height — {selected.height}px</label>
                            <input type="range" min={10} max={page.height} step={1} aria-label="Overlay height" value={selected.height} onChange={e => updateOverlay(selected.id, { height: Number(e.target.value) })} style={{ width: '100%' }} />
                            <button type="button" onClick={deleteSelected} style={{ ...btnStyle(colors), color: '#EF4444', borderColor: '#EF4444', marginTop: '6px' }}>🗑️ Delete this overlay</button>
                        </div>
                    )}

                    <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '10px' }}>
                        <span style={{ ...labelStyle, color: colors.text }}>Page cleanup</span>
                        <label style={{ ...labelStyle, color: colors.textSecondary }}>Brightness — {page.brightness}%</label>
                        <input type="range" min={50} max={150} step={1} aria-label="Page brightness" value={page.brightness} onChange={e => onChange({ ...state, background: { ...page, brightness: Number(e.target.value) } })} style={{ width: '100%' }} />
                        <label style={{ ...labelStyle, color: colors.textSecondary }}>Contrast — {page.contrast}%</label>
                        <input type="range" min={50} max={150} step={1} aria-label="Page contrast" value={page.contrast} onChange={e => onChange({ ...state, background: { ...page, contrast: Number(e.target.value) } })} style={{ width: '100%' }} />
                        <div style={{ display: 'flex', gap: '6px', margin: '6px 0' }}>
                            <button type="button" style={btnStyle(colors)} onClick={() => applyRotate('ccw')}>⟲ Rotate left</button>
                            <button type="button" style={btnStyle(colors)} onClick={() => applyRotate('cw')}>⟳ Rotate right</button>
                        </div>
                        <span style={{ ...labelStyle, color: colors.textSecondary }}>Crop (trim % from each edge)</span>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 10px' }}>
                            {(['left', 'top', 'right', 'bottom'] as const).map(side => (
                                <label key={side} style={{ fontSize: '11px', color: colors.textSecondary }}>
                                    {side} {crop[side]}%
                                    <input type="range" min={0} max={40} step={1} aria-label={`Crop ${side}`} value={crop[side]} onChange={e => setCrop(c => ({ ...c, [side]: Number(e.target.value) }))} style={{ width: '100%' }} />
                                </label>
                            ))}
                        </div>
                        <button type="button" style={{ ...btnStyle(colors), marginTop: '6px' }} onClick={applyCrop} disabled={crop.left + crop.right === 0 && crop.top + crop.bottom === 0}>✂️ Apply crop</button>
                    </div>
                </div>
            </div>

            {/* Export: flattened, at the page's own resolution. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button type="button" onClick={() => handleExport('pdf')} disabled={exportBusy} style={btnStyle(colors)}>📄 PDF</button>
                    <button type="button" onClick={() => handleExport('png')} disabled={exportBusy} style={btnStyle(colors)}>🖼️ PNG</button>
                    <span style={{ color: colors.textSecondary, fontSize: '11px' }}>
                        Exports flatten your edits onto your page at its own resolution ({facts.widthPx} × {facts.heightPx} px) — a photo is never upscaled or dressed up as print DPI it doesn't have. The PDF page is sized at {facts.pdfDpi} DPI from those pixels.
                    </span>
                </div>
                <span style={{ color: colors.textSecondary, fontSize: '11px' }}>💾 Save with “Save Design” above — it keeps this document in this browser and, when you are signed in, in your account.</span>
            </div>
            {exportNote && (
                <div style={{ marginTop: '10px', padding: '8px 10px', borderRadius: '8px', background: `${colors.border}50`, color: colors.text, fontSize: '12px' }}>{exportNote}</div>
            )}
        </div>
    );
};

export default DocumentRefresh;
